<template>
    <div class="step-content">
        <slot name="title">
            <StepTitle
                :active="active"
                :error="error"
                :complete="complete"
                :title="$t('email.broadcast.editor.sectionTitle.content')"
            />
        </slot>

        <div class="main-content">
            <img v-if="!hasContent" src="/images/email-content.svg" data-qa="blank-content" />

            <div v-else class="card-html-frame">
                <iframe
                    scrolling="no"
                    sandbox="allow-scripts"
                    frameborder="0"
                    loading="lazy"
                    :srcdoc="html"
                />

                <div class="preview-overlay">
                    <DsTextButton
                        leading-icon="eye-show"
                        class="preview-button"
                        :loading="isLoadingPreviewSelection"
                        @click="previewHtmlForBroadcast"
                    >
                        {{ $t('global.preview') }}
                    </DsTextButton>
                </div>
            </div>

            <div v-if="showMessages" class="content-risk">
                <h3 class="spam-issues-found-text">
                    {{ $tc('email.broadcast.steps.content.issuesFound', contentRiskMessages.length, { issueCount: contentRiskMessages.length }) }}
                </h3>

                <ContentRiskMessages
                    dismissible
                    class="content-risk-messages"
                    :quality="contentRiskQuality"
                    :messages="contentRiskMessages"
                >
                    <strong>{{ $tc(i18nBoldPath, contentRiskMessages.length, { messages: contentRiskMessages.length }) }}</strong>
                    <span>{{ ` ${$t(i18nInstructionPath)}` }}</span>
                </ContentRiskMessages>
            </div>
        </div>

        <ContentPreviewModal
            data-qa="broadcast-edit-preview-modal"
            :preview-html="htmlToPreview"
            :is-loading-preview-selection="isLoadingPreviewSelection"
            @close="htmlToPreview = null"
        />

        <slot name="action" :actionText="$t('email.broadcast.steps.content.actionText')" />
    </div>
</template>

<script>
import StepTitle from '@/components/unlayer/UnlayerEmailStepTitle.vue';
import ContentPreviewModal from '@/components/unlayer/ContentPreviewModal.vue';
import ContentRiskMessages from '@/components/unlayer/ContentRiskMessages.vue';
import { CONTENT_RISK_QUALITY } from '@/components/unlayer/content-risk.constants';

export default {
    components: {
        StepTitle,
        ContentPreviewModal,
        ContentRiskMessages,
    },
    props: {
        active: Boolean,
        error: Boolean,
        complete: Boolean,
        hasContent: Boolean,
        html: String,
        contentRiskQuality: String,
        contentRiskMessages: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            isLoadingPreviewSelection: false,
            htmlToPreview: null,
        };
    },

    computed: {
        i18nInstructionPath() {
            return this.contentRiskQuality === CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE
                ? 'email.broadcast.steps.content.inlineAlert.highRiskInstruction'
                : 'email.broadcast.steps.content.inlineAlert.moderateRiskInstruction';
        },

        i18nBoldPath() {
            return this.contentRiskQuality === CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE
                ? 'email.broadcast.steps.content.inlineAlert.highRiskIssues'
                : 'email.broadcast.steps.content.inlineAlert.moderateRiskIssues';
        },

        showMessages() {
            return this.hasContent
                && this.contentRiskMessages.length
                && [CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE, CONTENT_RISK_QUALITY.WARN].includes(this.contentRiskQuality);
        },
    },

    methods: {
        async previewHtmlForBroadcast() {
            this.isLoadingPreviewSelection = true;

            let resolve;
            let reject;
            const promise = new Promise((_resolve, _reject) => {
                resolve = _resolve;
                reject = _reject;
            });

            this.$emit('load-preview', {
                resolve,
                reject,
            });

            try {
                this.htmlToPreview = await promise;
            } catch (e) {
                this.$error({
                    message: this.$t('email.broadcast.steps.content.errorPreviewingTemplate'),
                    bottom: true,
                });
            } finally {
                this.isLoadingPreviewSelection = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import './steps';
$card-size: px-to-rem(300);

.main-content {
    display: flex;
}

.card-html-frame {
    position: relative;
    height: $card-size;
    width: $card-size;

    iframe {
        height: $card-size * 2;
        width: $card-size * 2;
        transform: scale(.5);
        transform-origin: 0 0;
    }

    &:hover .preview-overlay {
        display: flex;
    }
}

.preview-overlay {
    display: none;
    height: $card-size;
    width: $card-size;
    position: absolute;
    top: 0;
    left: 0;
    align-items: center;
    justify-content: center;
    background-color: rgba($color-paper, .7);
}

.content-risk {
    @include margin-start($spacing-200);
}

.spam-issues-found-text {
    font-size: $font-size-md;
    font-weight: $font-weight-bold;
    margin-bottom: $spacing-200;
}

@media($small) {
    .main-content {
        flex-direction: column;
    }

    .content-risk {
        @include margin-start(0);
        margin-top: $spacing-200;
    }
}
</style>
